// Рабочий лист «чего не хватает, чтобы месяц посчитался»: тарифы пачкой.
//
// ## Зачем эта панель
//
// Замер боевой базы 17.09.2026: назначений без суммы за период, по которым в июле-августе есть
// отработанные часы, — 247 у 154 человек на 46 объектах. Пока они пустые, ведомость по этим
// людям не считается вовсе, а утвердить месяц не даёт блокирующая находка «нет тарифа».
//
// Разбирать это по одному в карточках — 247 заходов. Заказчик их не сделает, и правильно.
//
// ## Почему норма спрашивается, а не выводится
//
// Три способа вывести её проверены на его собственных данных, и все три отброшены. Из поста:
// у него бывает 168 часов в сутки — это человеко-часы всего поста, и норма вышла бы 5 208,
// то есть зарплата в семь раз меньше. Из длины смен: совпало семь раз из восемнадцати. Из
// майской книги: 132 нормы из 187 там невозможные, это нормы объектов.
//
// Поэтому норму задаёт он. Мы показываем рядом ФАКТ — сколько человек отработал по табелю, —
// и его же прежнее решение по этому объекту, если оно есть. Толкуем ноль.
//
// ## Вставка из книги: ничего не пишется, пока он не увидел отчёт
//
// Тот же приём, что у загрузки официального периода. Вставка только ЗАПОЛНЯЕТ поля в списке и
// сообщает, сколько строк легло, сколько не нашлось и сколько оказалось спорными. Запись —
// отдельное нажатие.

import { useMemo, useState } from 'react';

import { RiAlertLine, RiCheckboxCircleLine } from '@remixicon/react';

import type { BulkRateOut, IncompleteRateOut } from '@/shared/api/endpoints/people';
import type { ApiError } from '@/shared/errors/api-error';
import { useAuthStore } from '@/shared/auth/store';
import { formatHours } from '@/shared/hours/format';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import { parseRublesToKopecks } from '@/shared/money/parse';
import { can } from '@/shared/rbac/permissions';
import { countLabel } from '@/shared/text/plural';
import { formatPeriodTitle, parsePeriod } from '@/shared/time/period';
import * as Button from '@/shared/ui/button';
import { DateField } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { TextArea } from '@/shared/ui/text-area';
import { TextField } from '@/shared/ui/text-field';

import { useChangeRateBulk, useIncompleteRate } from '../queries';

import { ключПоиска, ключФамилии, разобратьТарифы } from './тарифы-вставка';

const EDIT_PERMISSION = 'core.rate.edit';
/** Больше человеку в месяце не отработать: 24 часа в сутки 31 день. */
const ЧАСОВ_В_МЕСЯЦЕ = 744;

/** Что человек ввёл по одному назначению. Пока не записано — живёт только здесь. */
interface Черновик {
  сумма: string;
  норма: string;
}

export function TariffPanel({ id }: { id: string }) {
  // Опознаватель панели несёт период и, когда открыли из ведомости, объект: «2026-07» либо
  // «2026-07~объект». Ссылка на панель тогда открывается тем же отбором, что и была.
  //
  // Разделитель волной, а не двоеточием: двоеточие в адресе отделяет ВИД панели от её
  // опознавателя (`?p=вид:id`), и второе внутри опознавателя разобралось бы не туда.
  const [ключПериода, объект] = id.split('~');
  const период = parsePeriod(ключПериода ?? '');
  const права = useAuthStore((state) => state.permissions);
  const можноПравить = can(права, EDIT_PERMISSION);

  const лист = useIncompleteRate(период, объект ?? null);
  const запись = useChangeRateBulk();

  const [черновики, setЧерновики] = useState<Record<string, Черновик>>({});
  const [основание, setОснование] = useState('');
  const [сДаты, setСДаты] = useState(
    () => `${период.year}-${String(период.month).padStart(2, '0')}-01`,
  );
  const [вставка, setВставка] = useState('');
  const [отчётВставки, setОтчётВставки] = useState<ОтчётВставки | null>(null);
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [итог, setИтог] = useState<BulkRateOut | null>(null);

  const строки = useMemo(() => лист.data?.items ?? [], [лист.data]);

  function правка(назначение: string, поле: keyof Черновик, значение: string) {
    setЧерновики((было) => ({
      ...было,
      [назначение]: { сумма: '', норма: '', ...было[назначение], [поле]: значение },
    }));
  }

  return (
    <div className='flex flex-col gap-4 p-5'>
      <p className='max-w-prose text-paragraph-sm text-text-sub-600'>
        Пока у назначения нет суммы за период и нормы часов, начислять не из чего: ведомость по
        такому человеку не считается, и месяц не утвердить. Норму задаёте вы — вывести её из данных
        нельзя, поэтому рядом показано, сколько человек отработал по табелю.
      </p>

      <ScreenState
        data={лист.data}
        error={лист.error}
        subject='назначения без тарифа'
        isEmpty={(данные) => данные.items.length === 0}
        onRetry={() => void лист.refetch()}
        loading={<Skeleton className='h-40 w-full' />}
        empty={
          <EmptyState
            icon={RiCheckboxCircleLine}
            title='Тариф заполнен везде'
            description={`За ${formatPeriodTitle(период).toLowerCase()} назначений без суммы за период и нормы часов не осталось. Пересчитайте период — суммы появятся.`}
          />
        }
      >
        {(данные) => (
          <>
            <div className='text-paragraph-sm text-text-strong-950'>{данные.summary}</div>

            <ВставкаИзКниги
              значение={вставка}
              onЗначение={setВставка}
              отчёт={отчётВставки}
              onРазобрать={() => {
                const разбор = разложить(разобратьТарифы(вставка), строки);
                setОтчётВставки(разбор.отчёт);
                setЧерновики((было) => ({ ...было, ...разбор.черновики }));
              }}
            />

            <ul className='flex flex-col divide-y divide-stroke-soft-200'>
              {данные.items.map((строка) => (
                <СтрокаТарифа
                  key={строка.assignment_id}
                  строка={строка}
                  черновик={черновики[строка.assignment_id]}
                  onПравка={(поле, значение) => правка(строка.assignment_id, поле, значение)}
                />
              ))}
            </ul>

            <div className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-4'>
              <div className='flex flex-wrap items-end gap-2'>
                <div className='w-40'>
                  <DateField
                    id='tariff-from'
                    label='Тариф действует с'
                    value={сДаты}
                    onChange={setСДаты}
                  />
                </div>
                <div className='min-w-56 flex-1'>
                  <TextField
                    id='tariff-basis'
                    label='Основание'
                    value={основание}
                    onChange={setОснование}
                    // Месяц — тот, что открыт: подсказка «за июль» стояла и в августе.
                    placeholder={`тариф за ${formatPeriodTitle(период).toLowerCase()} по книге заказчика`}
                  />
                </div>
                <Button.Root
                  type='button'
                  variant='primary'
                  mode='filled'
                  size='small'
                  onClick={() => {
                    const проверка = собрать(черновики, строки, {
                      можноПравить,
                      основание,
                      сДаты,
                    });
                    if (типОшибки(проверка)) {
                      setЗапрет(проверка.запрет);
                      return;
                    }
                    setЗапрет(null);
                    запись.mutate(
                      { effective_from: сДаты, basis: основание.trim(), items: проверка.позиции },
                      {
                        onSuccess: (ответ) => {
                          setИтог(ответ);
                          setЧерновики({});
                          setОтчётВставки(null);
                          setВставка('');
                        },
                      },
                    );
                  }}
                >
                  {запись.isPending ? 'Записываем…' : 'Записать тариф'}
                </Button.Root>
              </div>
              {/* Вторая фраза — про ловушку, которую ставит заслон по дате
                  (`repositories/payroll.py`, `pull_rate_from_assignment`, решение 17.09.2026):
                  тариф «с 01.09» не дозаполняет пустые строки уже созданных августовских ведомостей.
                  Сам заслон верен — сентябрьская цена не должна молча менять август, — но лист,
                  открытый из «Дел», предлагает дату расчётного месяца, а заказчик после очистки
                  данных 05.10.2026 вносит прошлые. Говорим об этом там, где выбирают дату. */}
              <span className='text-paragraph-xs text-text-sub-600'>
                Основание и дата — одни на всю пачку: писать одно и то же основание у каждого
                человека никто не станет, а без основания тариф не меняется. Ведомости, уже
                созданные за месяцы раньше этой даты, тариф не заполнит: вносите прошлый месяц —
                ставьте дату с его первого числа.
              </span>
            </div>
          </>
        )}
      </ScreenState>

      {запрет !== null && <Hint.Root hasError>{запрет}</Hint.Root>}
      {запись.error !== null && <ErrorAlert error={запись.error as ApiError} />}
      {итог !== null && <ИтогЗаписи итог={итог} />}
    </div>
  );
}

function СтрокаТарифа({
  строка,
  черновик,
  onПравка,
}: {
  строка: IncompleteRateOut;
  черновик: Черновик | undefined;
  onПравка: (поле: keyof Черновик, значение: string) => void;
}) {
  const сумма = черновик?.сумма ?? '';
  const норма = черновик?.норма ?? '';
  const естьПодсказка =
    строка.suggested_period_amount !== null &&
    строка.suggested_period_amount !== undefined &&
    строка.suggested_norm_hours !== null &&
    строка.suggested_norm_hours !== undefined;

  return (
    <li className='flex flex-wrap items-end gap-2 py-2.5'>
      <div className='flex min-w-56 flex-1 flex-col'>
        <span className='text-paragraph-sm text-text-strong-950'>{строка.employee_name}</span>
        <span className='text-paragraph-xs text-text-sub-600'>
          {строка.site_name} · {строка.post_name} · {строка.missing}
          {/* Факт часов стоит рядом с полем нормы НАМЕРЕННО: из него человек и выводит норму.
              Мы её не выводим — проверено, что по факту она не восстанавливается. */}
          {строка.hours_fact !== null && строка.hours_fact !== undefined
            ? ` · по табелю ${formatHours(строка.hours_fact)} ч`
            : ' · смен в табеле нет'}
        </span>
      </div>
      <div className='w-36'>
        <TextField
          id={`tariff-amount-${строка.assignment_id}`}
          label='Сумма за период'
          value={сумма}
          onChange={(значение) => onПравка('сумма', значение)}
          inputMode='decimal'
        />
      </div>
      <div className='w-28'>
        <TextField
          id={`tariff-norm-${строка.assignment_id}`}
          label='Норма часов'
          value={норма}
          onChange={(значение) => onПравка('норма', значение)}
          inputMode='decimal'
        />
      </div>
      {естьПодсказка && (
        <Button.Root
          type='button'
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={() => {
            onПравка('сумма', formatKopecks(строка.suggested_period_amount ?? 0));
            onПравка('норма', строка.suggested_norm_hours ?? '');
          }}
        >
          {/* Это не наша догадка, а его собственное прежнее решение по ЭТОМУ объекту, и
              подписано оно именно так. Подставляется в поле, а не записывается: подтверждает он. */}
          Как было: {formatRubles(строка.suggested_period_amount ?? 0)} /{' '}
          {строка.suggested_norm_hours}
        </Button.Root>
      )}
    </li>
  );
}

function ВставкаИзКниги({
  значение,
  onЗначение,
  отчёт,
  onРазобрать,
}: {
  значение: string;
  onЗначение: (значение: string) => void;
  отчёт: ОтчётВставки | null;
  onРазобрать: () => void;
}) {
  const [открыто, setОткрыто] = useState(false);
  if (!открыто) {
    return (
      <div>
        <Button.Root
          type='button'
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={() => setОткрыто(true)}
        >
          Вставить из книги
        </Button.Root>
      </div>
    );
  }
  return (
    <div className='flex flex-col gap-2 rounded-lg bg-bg-weak-50 p-3'>
      <span className='text-paragraph-xs text-text-sub-600'>
        Три колонки в таком порядке: фамилия, сумма за период, норма часов. Порядок задан жёстко и
        не угадывается: из двух чисел в строке нельзя надёжно понять, где деньги, а где часы.
      </span>
      <TextArea
        id='tariff-paste'
        label='Вставьте из Excel'
        value={значение}
        onChange={onЗначение}
        rows={5}
        placeholder={'Абеленцев Н.\t85 000\t744\nБурукин Д.\t62 000\t372'}
      />
      <div>
        <Button.Root
          type='button'
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={onРазобрать}
        >
          Разложить по строкам
        </Button.Root>
      </div>
      {отчёт !== null && (
        <div className='flex flex-col gap-1 text-paragraph-xs text-text-sub-600'>
          <span className='text-text-strong-950'>
            Легло в {countLabel(отчёт.легло, ['строку', 'строки', 'строк'])}
            {отчёт.неНашли.length > 0 &&
              `, не нашли ${countLabel(отчёт.неНашли.length, ['строку', 'строки', 'строк'])}`}
            {отчёт.спорные.length > 0 &&
              `, спорных ${countLabel(отчёт.спорные.length, ['строка', 'строки', 'строк'])}`}
          </span>
          {/* Ничего ещё не записано: поля заполнены, и человек видит их перед нажатием.
              Молчаливая загрузка «как поняли» означала бы тариф, легший не на того. */}
          <span>
            Пока только заполнили поля — ничего не записано. Проверьте и нажмите «Записать».
          </span>
          {отчёт.неНашли.map((строка) => (
            <span key={`нет-${строка}`}>· не нашли в списке: {строка}</span>
          ))}
          {отчёт.спорные.map((строка) => (
            <span key={`спор-${строка}`}>· однофамильцы, разберите руками: {строка}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function ИтогЗаписи({ итог }: { итог: BulkRateOut }) {
  return (
    <div className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-4'>
      <span className='flex items-center gap-1.5 text-label-sm text-text-strong-950'>
        <RiCheckboxCircleLine className='size-4 shrink-0 text-success-base' aria-hidden />
        {итог.summary}
      </span>
      {итог.applied > 0 && (
        <span className='text-paragraph-xs text-text-sub-600'>
          Теперь пересчитайте период — суммы появятся в ведомостях.
        </span>
      )}
      {итог.skipped.map((строка) => (
        <span
          key={строка.assignment_id}
          className='flex items-start gap-1.5 text-paragraph-xs text-text-sub-600'
        >
          <RiAlertLine className='mt-0.5 size-4 shrink-0 text-error-base' aria-hidden />
          <span>
            <span className='text-text-strong-950'>{строка.label}</span> — {строка.reason}
          </span>
        </span>
      ))}
    </div>
  );
}

// ── Чистые части: их гоняют проверки без экрана ───────────────────────────────────────────

export interface ОтчётВставки {
  легло: number;
  неНашли: string[];
  спорные: string[];
}

/**
 * Разложить вставленное по строкам списка.
 *
 * Ищем по фамилии с первым инициалом, а когда инициала нет ни в книге, ни в программе — по
 * одной фамилии. Двое под одним ключом — это однофамильцы, и подставлять им тариф молча
 * нельзя: у заказчика сорок сотрудников с однофамильцами, и ошибка здесь означает чужую
 * зарплату.
 */
export function разложить(
  вставленное: { фамилия: string; сумма: string; норма: string; исходная: string }[],
  строки: IncompleteRateOut[],
): { черновики: Record<string, Черновик>; отчёт: ОтчётВставки } {
  const поКлючу = new Map<string, IncompleteRateOut[]>();
  const поФамилии = new Map<string, IncompleteRateOut[]>();
  for (const строка of строки) {
    добавить(поКлючу, ключПоиска(строка.employee_name), строка);
    добавить(поФамилии, ключФамилии(строка.employee_name), строка);
  }

  const черновики: Record<string, Черновик> = {};
  const неНашли: string[] = [];
  const спорные: string[] = [];
  let легло = 0;

  for (const запись of вставленное) {
    if (запись.фамилия === '' || запись.сумма === '' || запись.норма === '') {
      неНашли.push(запись.исходная);
      continue;
    }
    const точные = поКлючу.get(ключПоиска(запись.фамилия)) ?? [];
    const кандидаты =
      точные.length > 0 ? точные : (поФамилии.get(ключФамилии(запись.фамилия)) ?? []);
    if (кандидаты.length === 0) {
      неНашли.push(запись.исходная);
      continue;
    }
    if (кандидаты.length > 1) {
      спорные.push(запись.исходная);
      continue;
    }
    черновики[кандидаты[0]!.assignment_id] = { сумма: запись.сумма, норма: запись.норма };
    легло += 1;
  }
  return { черновики, отчёт: { легло, неНашли, спорные } };
}

function добавить(
  карта: Map<string, IncompleteRateOut[]>,
  ключ: string,
  строка: IncompleteRateOut,
) {
  if (ключ === '') return;
  const было = карта.get(ключ) ?? [];
  было.push(строка);
  карта.set(ключ, было);
}

interface ОшибкаСбора {
  запрет: string;
}
interface УспехСбора {
  позиции: {
    assignment_id: string;
    rate_mode: string;
    period_amount: number;
    norm_hours: string;
  }[];
}

function типОшибки(итог: ОшибкаСбора | УспехСбора): итог is ОшибкаСбора {
  return 'запрет' in итог;
}

/**
 * Собрать заполненное в пачку и проверить, что отправлять.
 *
 * Проверки стоят ДО отправки не ради экономии запроса, а ради слов: сервер скажет «норма
 * больше, чем есть в месяце» по одной строке, а человек в этот момент смотрит на двести сорок
 * семь. Назвать ошибку рядом с полем, где она сделана, — дешевле на один заход.
 */
export function собрать(
  черновики: Record<string, Черновик>,
  строки: IncompleteRateOut[],
  условия: { можноПравить: boolean; основание: string; сДаты: string },
): ОшибкаСбора | УспехСбора {
  if (!условия.можноПравить) {
    return { запрет: 'Тариф меняет администратор или финансовый отдел.' };
  }
  if (условия.основание.trim() === '') {
    return {
      запрет: 'Напишите основание: без него тариф не меняется, так просил сам заказчик.',
    };
  }
  if (условия.сДаты === '') {
    return { запрет: 'Назовите день, с которого действует тариф.' };
  }

  const позиции: УспехСбора['позиции'] = [];
  for (const строка of строки) {
    const черновик = черновики[строка.assignment_id];
    if (черновик === undefined) continue;
    const сумма = черновик.сумма.trim();
    const норма = черновик.норма.trim();
    if (сумма === '' && норма === '') continue;
    if (сумма === '' || норма === '') {
      return {
        запрет: `${строка.employee_name}: нужны оба числа — сумма за период и норма часов. Ставка считается от них.`,
      };
    }
    const разбор = parseRublesToKopecks(сумма);
    if (!разбор.ok || разбор.value <= 0) {
      return { запрет: `${строка.employee_name}: сумма пишется числом — 85 000 или 85 000,50.` };
    }
    const часы = Number(норма.replace(',', '.'));
    if (!Number.isFinite(часы) || часы <= 0) {
      return { запрет: `${строка.employee_name}: норма часов пишется числом — 744 или 372.` };
    }
    if (часы > ЧАСОВ_В_МЕСЯЦЕ) {
      return {
        запрет: `${строка.employee_name}: норма ${норма} больше, чем часов в месяце. Двадцать четыре часа в сутки тридцать один день — это 744. Похоже, сюда попала норма ОБЪЕКТА.`,
      };
    }
    позиции.push({
      assignment_id: строка.assignment_id,
      rate_mode: 'by_period',
      period_amount: разбор.value,
      norm_hours: норма.replace(',', '.'),
    });
  }
  if (позиции.length === 0) {
    return { запрет: 'Ничего не заполнено: впишите сумму и норму хотя бы одному человеку.' };
  }
  return { позиции };
}
